import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, Mail, CalendarCheck2, ShieldAlert, CheckCircle2, XCircle,
  HelpCircle, ExternalLink, ChevronDown, ChevronUp, Info,
} from "lucide-react";
import { ScoreGauge } from "@/components/ScoreGauge";
import { useI18n } from "@/lib/i18n";
import { api, trackEvent } from "@/lib/api";
import { toast } from "sonner";

const STATUS_ICON = {
  VERIFIED: CheckCircle2,
  NOT_DETECTED: XCircle,
  UNKNOWN: HelpCircle,
};
const STATUS_CLASS = {
  VERIFIED: "priority-low",
  NOT_DETECTED: "priority-high",
  UNKNOWN: "priority-medium",
};

const PriorityBadge = ({ priority }) => {
  const { t } = useI18n();
  const cls =
    priority === "high" ? "priority-high" :
    priority === "medium" ? "priority-medium" : "priority-low";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase ${cls}`} data-testid={`priority-badge-${priority}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {t(`results.priority.${priority}`)}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  const Icon = STATUS_ICON[status] || HelpCircle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase ${STATUS_CLASS[status]}`}>
      <Icon size={12} strokeWidth={2.4} />
      {t(`results.status.${status}`)}
    </span>
  );
};

const FactRow = ({ fact }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const hasEvidence = fact.evidence || fact.source_url;
  const translatedLabel = t(`results.factLabels.${fact.key}`);
  const label = translatedLabel && translatedLabel !== `results.factLabels.${fact.key}` ? translatedLabel : fact.label;
  return (
    <div className="border-b border-[color:var(--impact-border)] last:border-0 py-3" data-testid={`fact-row-${fact.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[14px]">{label}</div>
            <StatusBadge status={fact.status} />
            {fact.scoring === false && (
              <span data-testid={`display-only-pill-${fact.key}`} className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)] uppercase tracking-widest text-[color:var(--impact-ink-2)]">
                {t("results.displayOnly")}
              </span>
            )}
            {fact.confidence !== "high" && (
              <span className="text-[10px] uppercase tracking-widest text-[color:var(--impact-ink-2)]">
                {fact.confidence === "medium" ? "conf. moyenne" : "conf. faible"}
              </span>
            )}
          </div>
          {fact.value !== null && fact.value !== undefined && fact.status === "VERIFIED" && (
            <div className="text-[13px] text-[color:var(--impact-ink-2)] mt-1 break-words">
              {typeof fact.value === "object" ? JSON.stringify(fact.value) : String(fact.value)}
            </div>
          )}
        </div>
        {hasEvidence && (
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid={`fact-evidence-toggle-${fact.key}`}
            className="text-xs font-semibold text-[color:var(--impact-brand)] flex items-center gap-1 shrink-0"
          >
            {open ? t("results.hideEvidence") : t("results.seeEvidence")}
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>
      {open && hasEvidence && (
        <div className="mt-3 p-3 rounded-lg bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)] text-[13px]">
          {fact.evidence && (
            <div className="text-[color:var(--impact-ink)] mb-2 break-words">{fact.evidence}</div>
          )}
          {fact.source_url && (
            <a href={fact.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[color:var(--impact-brand)] animated-underline">
              <ExternalLink size={12} /> {t("results.source")} : {fact.source_url}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const CategoryCard = ({ cat, narrative, facts }) => {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const catFacts = useMemo(() => facts.filter((f) => f.category === cat.key), [facts, cat.key]);
  const hasScore = cat.score !== null && cat.score !== undefined;
  const coverageText = t("results.coverageTemplate")
    .replace("{checked}", cat.coverage_checked)
    .replace("{total}", cat.coverage_total);
  return (
    <div className="impact-card p-7 md:p-8" data-testid={`category-${cat.key}`}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="eyebrow mb-2">{t(`results.dimensions.${cat.key}`)}</div>
          <div className="flex items-baseline gap-2 mb-3">
            {hasScore ? (
              <>
                <div className="font-display font-black text-4xl md:text-5xl tabular-nums">{cat.score}</div>
                <div className="text-sm text-[color:var(--impact-ink-2)]">/ 100</div>
              </>
            ) : (
              <div className="text-sm text-[color:var(--impact-ink-2)] italic">{t("results.noScoreYet")}</div>
            )}
          </div>
          <PriorityBadge priority={narrative?.priority || "medium"} />
          <div className="mt-4 text-[13px] text-[color:var(--impact-ink-2)]">
            {t("results.coverage")} : <span className="font-semibold text-[color:var(--impact-ink)]">{coverageText}</span>
            {cat.unknown_count > 0 && (
              <span className="ml-1">· {cat.unknown_count} à confirmer</span>
            )}
          </div>
          {cat.scored_criteria_labels?.length > 0 && (
            <div className="mt-3 text-[12px] text-[color:var(--impact-ink-2)]" data-testid={`scored-criteria-${cat.key}`}>
              <span className="eyebrow text-[10px]">{t("results.scoredCriteria")} :</span>{" "}
              <span>{cat.scored_criteria_labels.join(" · ")}</span>
            </div>
          )}
          {narrative?.explanation && (
            <p className="text-[color:var(--impact-ink-2)] mt-4 text-[15px] leading-relaxed">{narrative.explanation}</p>
          )}
        </div>
        {hasScore && (
          <div className="w-24 shrink-0">
            <ScoreGauge value={cat.score} size={96} stroke={8} />
          </div>
        )}
      </div>

      {cat.key === "paid_acquisition_readiness" && (
        <div className="mt-5 flex items-start gap-2 text-[12px] text-[color:var(--impact-ink-2)] bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)] rounded-lg p-3">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>{t("results.paidNotice")}</span>
        </div>
      )}

      {(narrative?.strengths?.length > 0 || narrative?.opportunities?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[color:var(--impact-border)]">
          <div>
            <div className="eyebrow mb-2">{t("results.strengths")}</div>
            <ul className="text-sm space-y-1.5 text-[color:var(--impact-ink)]">
              {(narrative?.strengths || []).map((x, i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 size={14} className="mt-1 text-[color:var(--impact-low)] shrink-0" /><span>{x}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-2">{t("results.opportunities")}</div>
            <ul className="text-sm space-y-1.5 text-[color:var(--impact-ink)]">
              {(narrative?.opportunities || []).map((x, i) => (
                <li key={i} className="flex gap-2"><XCircle size={14} className="mt-1 text-[color:var(--impact-high)] shrink-0" /><span>{x}</span></li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="mt-5">
        <button
          onClick={() => setExpanded((x) => !x)}
          data-testid={`toggle-facts-${cat.key}`}
          className="text-xs font-semibold text-[color:var(--impact-brand)] inline-flex items-center gap-1"
        >
          {expanded ? t("results.hideEvidence") : t("results.factsTitle")}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {expanded && (
          <div className="mt-3 border-t border-[color:var(--impact-border)]">
            {catFacts.map((f) => <FactRow key={f.key} fact={f} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export const ResultsDashboard = ({ audit }) => {
  const { t } = useI18n();
  const { result, company } = audit;
  const facts = result.facts || [];
  const cats = result.category_scores || [];
  const narrativesByKey = useMemo(
    () => Object.fromEntries((result.category_narratives || []).map((n) => [n.key, n])),
    [result.category_narratives]
  );
  const factByKey = useMemo(
    () => Object.fromEntries(facts.map((f) => [f.key, f])),
    [facts]
  );

  const emailReport = async () => {
    try {
      const { data } = await api.post(`/audits/${audit.id}/email-report`, { audit_id: audit.id });
      if (data?.skipped) {
        toast.info("Envoi différé — configuration courriel non complétée.");
      } else {
        toast.success(t("results.emailSent"));
      }
      trackEvent("report_viewed", { audit_id: audit.id, action: "email" });
    } catch {
      toast.error("Erreur / Error");
    }
  };
  const bookCall = () => {
    trackEvent("consultation_clicked", { audit_id: audit.id });
    window.open(
      "https://agenceimpactdigital.ca/contact?utm_source=prospection&utm_medium=email&utm_campaign=septembre_2026&utm_content=cta_consultation",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div data-testid="results-dashboard" className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="eyebrow mb-3">{t("results.badge")}</div>
        <h1 className="font-display font-bold tracking-tight text-3xl md:text-5xl mb-2" data-testid="results-title">
          {company.company_name}
        </h1>
        <a href={result.crawl?.home_url || company.website_url} target="_blank" rel="noreferrer" className="text-[color:var(--impact-ink-2)] animated-underline">
          {result.crawl?.home_url || company.website_url}
        </a>
        {result.crawl?.reachable ? (
          <div className="mt-2 text-[13px] text-[color:var(--impact-ink-2)]">
            {result.crawl.pages_analyzed_count} {t("results.pagesAnalyzed")}
          </div>
        ) : (
          <div className="mt-4 impact-card p-4 border-l-4" style={{ borderLeftColor: "var(--impact-high)" }}>
            <div className="flex items-start gap-3 text-sm">
              <ShieldAlert size={16} className="mt-0.5 shrink-0 text-[color:var(--impact-high)]" />
              <span>{t("results.unreachableBanner")}</span>
            </div>
          </div>
        )}
        {result.crawl?.redirect_notice && (
          <div className="mt-3 impact-card p-4 border-l-4" style={{ borderLeftColor: "var(--impact-medium)" }} data-testid="redirect-notice">
            <div className="flex items-start gap-3 text-sm">
              <Info size={16} className="mt-0.5 shrink-0 text-[color:var(--impact-medium)]" />
              <span>{result.crawl.redirect_notice}</span>
            </div>
          </div>
        )}
      </motion.div>

      {/* Executive summary */}
      {result.executive_summary && (
        <div className="mt-10 impact-card p-8 md:p-10">
          <div className="eyebrow mb-3">01 — {t("results.execSummary")}</div>
          <p className="font-display font-medium text-xl md:text-2xl leading-snug text-[color:var(--impact-ink)]" data-testid="executive-summary">
            {result.executive_summary}
          </p>
          <div className="flex items-start gap-3 text-sm text-[color:var(--impact-ink-2)] mt-5">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <p>{t("results.disclaimer")}</p>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="mt-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="eyebrow mb-2">02</div>
            <h3 className="font-display font-bold text-2xl md:text-3xl">{t("results.catTitle")}</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="category-grid">
          {cats.map((c) => (
            <CategoryCard key={c.key} cat={c} narrative={narrativesByKey[c.key]} facts={facts} />
          ))}
        </div>
      </div>

      {/* Top opportunities */}
      {result.top_opportunities?.length > 0 && (
        <div className="mt-16">
          <div className="eyebrow mb-2">03</div>
          <h3 className="font-display font-bold text-2xl md:text-3xl mb-6">{t("results.opportunitiesTitle")}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="top-opportunities">
            {result.top_opportunities.map((o, i) => (
              <div key={i} className="impact-card p-7 flex flex-col" data-testid={`opportunity-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="font-display font-black text-4xl text-[color:var(--impact-ink)]/15 leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <PriorityBadge priority={o.priority} />
                </div>
                <div className="font-display font-bold text-xl mb-3">{o.title}</div>
                <p className="text-[color:var(--impact-ink-2)] text-[15px] leading-relaxed mb-4 flex-1">{o.why_it_matters}</p>
                <div className="pt-4 border-t border-[color:var(--impact-border)]">
                  <div className="eyebrow mb-2">Action</div>
                  <p className="text-[15px] font-medium">{o.recommended_action}</p>
                </div>
                {o.based_on_fact_keys?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[color:var(--impact-border)]">
                    <div className="eyebrow mb-2">{t("results.basedOn")}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {o.based_on_fact_keys.map((k) => {
                        const tl = t(`results.factLabels.${k}`);
                        const label = tl && tl !== `results.factLabels.${k}` ? tl : (factByKey[k]?.label || k);
                        return (
                          <span key={k} className="text-[11px] px-2 py-1 rounded-full bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)]">
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Impact Digital recommendation */}
      {result.recommendations?.length > 0 && (
        <div className="mt-16 impact-card impact-noise p-8 md:p-12">
          <div className="eyebrow mb-2">04 — {t("results.recTitle")}</div>
          <ul className="mt-4 space-y-4" data-testid="recommendations">
            {result.recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-md bg-[color:var(--impact-ink)] text-white grid place-items-center shrink-0 font-display font-black text-sm">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-lg">
                    {t(`results.services.${r.service}`) === `results.services.${r.service}` ? r.label : t(`results.services.${r.service}`)}
                  </div>
                  <div className="text-[color:var(--impact-ink-2)] text-[15px] leading-relaxed">{r.reason}</div>
                  {r.based_on_fact_keys?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {r.based_on_fact_keys.map((k) => {
                        const tl = t(`results.factLabels.${k}`);
                        const label = tl && tl !== `results.factLabels.${k}` ? tl : (factByKey[k]?.label || k);
                        return (
                          <span key={k} className="text-[11px] px-2 py-1 rounded-full bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)]">
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Final CTA */}
      <div className="mt-16 impact-card p-8 md:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-8 items-center">
          <div>
            <h3 className="font-display font-bold text-2xl md:text-3xl mb-3">{t("results.finalTitle")}</h3>
            <p className="text-[color:var(--impact-ink-2)] max-w-xl">{t("results.finalBody")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={bookCall} data-testid="book-consult-btn" className="btn-primary btn-brand">
              <CalendarCheck2 size={18} /> {t("results.cta1")} <ArrowRight size={16} />
            </button>
            <button onClick={emailReport} data-testid="email-report-btn" className="btn-ghost">
              <Mail size={16} /> {t("results.cta2")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
